import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteDialog } from "@/components/delete-dialog";
import { useAuth } from "@/hooks/use-auth";
import {
  useDeleteProject,
  useDeleteProjectPermanent,
  useUpdateProjectStatus,
} from "@/hooks/use-projects";
import { canCreateProject, canDeleteProject } from "@/lib/api-types";
import type { Project } from "@/lib/api-types";
import { cn } from "@/lib/utils";
import { ProjectSettingsDialog } from "./project-settings-dialog";

type Props = {
  project: Project;
  className?: string;
  /** Forwarded to DeleteProjectDialog — pass a redirect when this menu lives on the project's own detail page. */
  onDeleted?: () => void;
};

export function ProjectCardMenu({ project, className, onDeleted }: Props) {
  const { role } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteProject = useDeleteProject();
  const permanentDeleteProject = useDeleteProjectPermanent();
  const updateStatus = useUpdateProjectStatus();

  // Both PATCH (rename/retention) and DELETE are gated to the same two roles
  // on the backend today (ACTIONS.CREATE_PROJECT / DELETE_PROJECT).
  const canEdit = canCreateProject(role);
  const canDelete = canDeleteProject(role);
  // Mark active/inactive lives on the DELETE_PROJECT permission tier
  // server-side (locked 2026-07-12).
  const canToggleStatus = canDelete;
  const isInactive = !!project.inactiveAt;

  // Empty menu = don't render. Now that Mark Inactive is real, this triggers
  // only when the viewer has no menu items at all.
  if (!canEdit && !canDelete && !canToggleStatus) return null;

  return (
    <div
      className={cn(className)}
      // ProjectCard wraps the whole card in a <Link>. Radix portals content
      // to document.body, but React re-bubbles synthetic events up the
      // component tree — stop it here so opening the menu/dialogs doesn't
      // also navigate the card.
      onClick={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Project options"
            className="grid h-7 w-7 place-items-center rounded-md bg-background/80 text-muted-foreground backdrop-blur transition-colors hover:bg-secondary hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Project</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {canToggleStatus && (
            <DropdownMenuItem
              onSelect={() =>
                updateStatus.mutate({ id: project.id, active: isInactive })
              }
              disabled={updateStatus.isPending}
            >
              {isInactive ? "Mark Active" : "Mark Inactive"}
            </DropdownMenuItem>
          )}

          {canEdit && (
            <DropdownMenuItem onSelect={() => setSettingsOpen(true)}>
              Project Settings
            </DropdownMenuItem>
          )}

          {canDelete && (
            <DropdownMenuItem
              onSelect={() => setDeleteOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canEdit && (
        <ProjectSettingsDialog project={project} open={settingsOpen} onOpenChange={setSettingsOpen} />
      )}
      {canDelete && (
        <DeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          itemLabel="project"
          itemName={project.name}
          onTrash={async () => {
            await deleteProject.mutateAsync(project.id);
            onDeleted?.();
          }}
          onPermanent={async () => {
            await permanentDeleteProject.mutateAsync(project.id);
            onDeleted?.();
          }}
        />
      )}
    </div>
  );
}
